/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'underscore',
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'com/ibm/vida/control/vida',
	'./VIPRConfig',
	'./VIPRLibraries',
	'./VIPRUtils',
	'./VIPRBundleHelper'
], function(_, Class, vida, VIPRConfig, VIPRLibraries, VIPRUtils, VIPRBundleHelper) {
	'use strict';

	const CUSTOM_PREVIEWS = ['visualizationPreview', 'com.ibm.vis.schematicsPreview'];
	var VIPRProxy = Class.extend({
		init: function () {
			this._setInitialStates();
		},

		_setInitialStates: function () {
			this._libs = undefined;
			//Only reset for custom libs so that they can be refreshed
			this._customLibs = undefined;
			if (!this.libraries) {
				this.libraries = {
					system: [],
					custom: []
				};
			} else {
				//Only reset for custom libs so that they can be refreshed
				this.libraries.custom = [];
			}
		},

		initialize: function(params = {}) {
			this.dashboardApi = params.dashboardApi;
			if (params.refresh) {
				this._setInitialStates();
			}

			if (!this._libs){
				this.ajaxSvc = params.dashboardApi.getGlassCoreSvc('.Ajax');
				return VIPRBundleHelper.getALLVIPRLibraries({ dashboardApi: params.dashboardApi })
					.then((libraries) => {
						const libsToCreate = this._getLibsBundleToCreate(libraries, params.refresh);
						return this._setRequiredConfigurationDefaults(libsToCreate)
							.then((configuredLibraries = []) => {
								configuredLibraries.forEach((lib) => {
									const bundleinfo = VIPRConfig.getConfig(lib.id);
									lib.config = {};
									// Add properties config if there is any
									if (bundleinfo){
										if (bundleinfo.isCustomVis && bundleinfo.bundleInclude) {
											lib.config['properties'] = bundleinfo.bundleInclude;
										} else if (bundleinfo.isCustomVis && !bundleinfo.bundleInclude) {
											// never has to get to this case
											const logger = this.dashboardApi.getGlassCoreSvc('.Logger');
											logger.error('`Failed to set lib.config.properties');
										} else if (!bundleinfo.isCustomVis && bundleinfo.config) {
											lib.config['properties'] = bundleinfo.config;
										}
									}
									// Add slots config if there is any.
									if (bundleinfo && bundleinfo.slotConfig) {
										lib.config['slots'] = bundleinfo.slotConfig;
									}
								});
								this._createLibs(configuredLibraries, params.refresh);
								this._setLibraries(configuredLibraries);
							});
					});
			}
			return Promise.resolve();
		},

		updateCustomLibrary: function(id) {
			const library = this._customLibs[id];
			if(library) {
				this._updateLibrary(library);
			}
		},

		getLibraries: function() {
			if (!this._libs) {
				const libs = {
					...this._systemLibs,
					...this._customLibs
				};
				this._libs = _.indexBy(libs, 'id');
			}
			return this._libs;
		},

		getSystemLibIds: function() {
			return this.libraries ? this.libraries.system.map(library => {
				return library.id;
			}) : [];
		},

		getCustomLibIds: function() {
			return this.libraries ? this.libraries.custom.map(library => {
				return library.id;
			}) : [];
		},

		getBundleIds: function(libType = VIPRLibraries.LIB_TYPE_ALL) {
			const ids = [];
			if (libType === VIPRLibraries.LIB_TYPE_ALL || libType === VIPRLibraries.LIB_TYPE_SYSTEM) {
				ids.push(...this.getSystemLibIds());
			}

			if (libType === VIPRLibraries.LIB_TYPE_ALL || libType === VIPRLibraries.LIB_TYPE_CUSTOM) {
				ids.push(...this.getCustomLibIds());
			}
			return ids;
		},

		_setLibraries: function(libraries = []) {
			if (!this._customLibs) {
				this._customLibs = {};
			}
			if (!this._systemLibs) {
				this._systemLibs = {};
			}

			libraries.forEach(library => {
				if (library.isSchematic  || library.isCustomVis || CUSTOM_PREVIEWS.indexOf(library.id) !== -1) {
					this._customLibs[library.id] = library;
					this.libraries.custom.push(library);
				} else {
					this._systemLibs[library.id] = library;
					this.libraries.system.push(library);
				}
			});
		},

		_getLibsBundleToCreate: function(libraries = [], refresh = false) {
			if (!refresh) {
				return libraries;
			}

			const libsToCreate = [];

			libraries.forEach(library => {
				if (library.isSchematic  || library.isCustomVis || CUSTOM_PREVIEWS.indexOf(library.id) !== -1) {
					libsToCreate.push(library);
				}
			});
			return libsToCreate;
		},

		_createLibs: function(configuredLibraries = [], refresh = false) {
			if (refresh) {
				configuredLibraries.forEach(library => {
					this._updateLibrary(library);
				});
			} else {
				_.extend(this, vida.VIPR.create({ bundles: configuredLibraries }));
			}
		},

		_updateLibrary: function(library) {
			const bundle = library || {};
			const isSchematic = bundle.isSchematic;
			if (bundle.id && !isSchematic) {
				this.unregister(bundle.id);
			}
			if (bundle.id && ((isSchematic && !this.isRegistered(bundle.id)) || !isSchematic)) {
				this.register(bundle.id, bundle.location, bundle.config, bundle.fileName, bundle);
			}
		},

		/**
		* @param {String} viprDefinitionId - Vis bundle id
		* @param {Object} requiredConfig - contains a list of required configuration tokens/properties
		* @returns {String} a definition id for a library that should be removed as the user/server doesnt have the required configuration(e.g. without mapbox tokens, the user will not be able to use maps)
		* Makes a rest call searching for specific configuration tokens on a user's server and sets the corresponding default properties
		*/
		_fetchAndApplyRequiredConfigurationDefaults: function({ viprDefinitionId, requiredConfig }){
			return Promise.reduce(requiredConfig, (values, key) => {
				return this.dashboardApi.getGlassCoreSvc('.Config').getConfigValue(key).then((value) => {
					values[key] = value;
					return values;
				});
			}, {}).then((response) => {
				if (Object.values(response).indexOf('default') === -1) {
					VIPRUtils.applyRequiredConfiguration(viprDefinitionId, requiredConfig, response);
					return null;
				}
				return viprDefinitionId;
			}).catch(() => {
				return viprDefinitionId;
			});
		},

		/*
		 * Check each library to see if there is a required configuration. If so, then call fetchAndApplyRequiredConfigurationDefaults
		 * which will gather up any viprdefinitionids that do not have the corresponding configuration tokens--in this case, the id will
		 * be removed from the libraries so that the user cannot use that type of visualization.
		*/
		_setRequiredConfigurationDefaults: function(libraries) {
			const libraryRequiredConfigs = libraries
				.map(lib => ({ viprDefinitionId: lib.id, requiredConfig: VIPRUtils.getRequiredConfig(lib.id) }))
				.filter(libraryRequiredConfig => !!libraryRequiredConfig.requiredConfig);

			return Promise.map(libraryRequiredConfigs, config => this._fetchAndApplyRequiredConfigurationDefaults(config))
				.filter(definition => !!definition)
				.then((definitionsToBeRemoved) => {
					return libraries.filter(lib => !_.contains(definitionsToBeRemoved, lib.id));
				});
		},

		createWidget: function(nodeId, hostAPI) {
			return new vida.SmallMultipleGrid( { vipr: this, hostAPI: hostAPI }, nodeId );
		},

		unloadBundle(id) {
			this.unload(id);
		},

		loadBundle: function(id) {
			return this.load(id)
				.then(function(bundle) {
					return vida.SmallMultipleGrid.extendVizBundle(bundle);
				})
				.catch((error) => {
					const logger = this.dashboardApi.getGlassCoreSvc('.Logger');
					if(id !== 'visualizationPreview') {
						logger.error(error);
					}
				});
		}
	});

	// create a singleton instance
	return new VIPRProxy();
});
